import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import InfluencerLayout from "@/components/InfluencerLayout";
import BearFluencerBadge from "@/components/BearFluencerBadge";
import PromoCodeDialog from "@/components/PromoCodeDialog";
import PromoCodeStats from "@/components/PromoCodeStats";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DollarSign,
  Users,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Clock,
  Copy,
  TrendingUp,
  Award,
  Link as LinkIcon,
  CreditCard,
  CalendarDays,
  Target,
  Percent,
  MousePointerClick,
  UserPlus,
  ShoppingCart,
  Download,
  FileText,
  Wallet,
  Info,
  Hash,
  BookOpen,
  Image,
  ChevronLeft,
  ChevronRight,
  Calendar,
  X,
  CheckCircle,
  HourglassIcon,
  Share2,
  BarChart3,
  FileDown,
  Sparkles,
  Activity,
  ArrowRight,
  Building2,
  Instagram,
  Twitter,
  Facebook
} from "lucide-react";
import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import type { Badge as BadgeData } from "@shared/types/badges";
import { 
  LineChart, 
  Line, 
  BarChart,
  Bar,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer,
  FunnelChart,
  Funnel,
  LabelList,
  Cell
} from 'recharts';
import { format, subMonths, startOfMonth } from 'date-fns';

// Match the actual API response structure
interface DashboardMetrics {
  lifetime: {
    clicks: number;
    signups: number;
    conversions: number;
    totalEarned: number;
    pendingCommission: number;
  };
  thisMonth: {
    clicks: number;
    signups: number;
    conversions: number;
    earned: number;
  };
  lastMonth: {
    clicks: number;
    signups: number;
    conversions: number;
    earned: number;
  };
}

interface RecentReferral {
  id: number;
  eventType: string;
  referredUserId?: number;
  commissionAmount?: number;
  commissionStatus?: string;
  createdAt: string;
}

interface StripeStatus {
  accountId?: string;
  onboardingComplete: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  requirements: string[];
}

interface InfluencerDashboardData {
  referralCode: string;
  referralLink: string;
  metrics: DashboardMetrics;
  recentReferrals: RecentReferral[];
  stripeStatus: StripeStatus;
  commissionRate: number;
  profile: {
    primaryPlatform?: string;
    tier?: string;
    featured?: boolean;
  };
  badges: BadgeData[];
}

// Promo Code Types
interface PromoCode {
  id: number;
  influencerId: number;
  code: string;
  description?: string;
  discountPercent: number;
  commissionBps: number;
  status: 'active' | 'inactive' | 'expired';
  maxRedemptions?: number;
  timesRedeemed: number;
  totalRevenue: string;
  totalCommissionEarned: string;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
  metadata?: any;
}

interface PromoCodeStats {
  totalCodes: number;
  activeCodes: number;
  inactiveCodes: number;
  totalUses: number;
  totalRevenue: number;
  totalCommission: number;
}

// Old dashboard format for backward compatibility
interface InfluencerData {
  referralCode: string;
  commissionRate: number;
  isApproved: boolean;
  totalEarnings: number;
  totalReferrals: number;
  pendingEarnings: number;
  onboardingCompleted?: boolean;
  payoutsEnabled?: boolean;
  chargesEnabled?: boolean;
  referralLink?: string;
}

interface AccountStatus {
  hasAccount: boolean;
  onboardingCompleted: boolean;
  payoutsEnabled: boolean;
  chargesEnabled: boolean;
  detailsSubmitted: boolean;
}

interface ReferralActivity {
  id: number | string;
  eventType: 'click' | 'signup' | 'subscription' | 'purchase' | 'manual_adjustment' | string;
  referredUserId?: number;
  customerEmail?: string;
  orderValue?: number;
  commissionAmount?: number;
  commissionStatus?: 'pending' | 'accrued' | 'paid' | string;
  eventData?: any;
  createdAt: string;
}

interface MonthlyEarning {
  month: string;
  earnings: number;
}

interface ConversionFunnel {
  name: string;
  value: number;
  fill: string;
}

export default function InfluencerDashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedPromoCode, setSelectedPromoCode] = useState<PromoCode | null>(null);
  const [promoStatsOpen, setPromoStatsOpen] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState<PromoCode | null>(null);

  // Try to get the new comprehensive dashboard data first
  const { data: dashboardDataNew, isLoading: dashboardLoadingNew, error: dashboardErrorNew } = useQuery<InfluencerDashboardData>({
    queryKey: ['/api/influencer/dashboard'],
    enabled: !!user,
    retry: (failureCount, error: any) => {
      if (error?.status === 404) return false;
      return failureCount < 1;
    }
  });

  // Fallback to old dashboard format for backward compatibility
  const { data: dashboardDataOld, isLoading: dashboardLoadingOld, error: dashboardErrorOld } = useQuery<InfluencerData>({
    queryKey: ['/api/influencer/dashboard-old'],
    enabled: !!user && !dashboardDataNew && !!dashboardErrorNew,
    queryFn: async () => {
      const response = await fetch('/api/influencer/dashboard');
      if (!response.ok) throw new Error('Failed to fetch dashboard');
      return response.json();
    },
    retry: false
  });

  // Combine loading states
  const dashboardLoading = dashboardLoadingNew || dashboardLoadingOld;
  const dashboardError = dashboardErrorNew && dashboardErrorOld;
  
  // Convert old format to new format if needed
  const dashboardData = useMemo(() => {
    if (dashboardDataNew?.metrics) {
      return dashboardDataNew;
    }
    if (dashboardDataOld) {
      // Old format - convert to match expected structure
      return {
        ...dashboardDataOld,
        metrics: {
          lifetime: {
            clicks: 0,
            signups: 0,
            conversions: dashboardDataOld.totalReferrals || 0,
            totalEarned: dashboardDataOld.totalEarnings || 0,
            pendingCommission: dashboardDataOld.pendingEarnings || 0
          },
          thisMonth: {
            clicks: 0,
            signups: 0,
            conversions: 0,
            earned: 0
          },
          lastMonth: {
            clicks: 0,
            signups: 0,
            conversions: 0,
            earned: 0
          }
        }
      };
    }
    return null;
  }, [dashboardDataNew, dashboardDataOld]);

  const { data: accountStatus, isLoading: statusLoading } = useQuery<AccountStatus>({
    queryKey: ['/api/influencer/account-status'],
    enabled: !!user && !!dashboardData,
    retry: false
  });

  // Fetch real referral activity data with pagination
  const { data: referralActivity, isLoading: referralsLoading } = useQuery<ReferralActivity[]>({
    queryKey: ['/api/influencer/referrals', { page: currentPage, limit: itemsPerPage }],
    enabled: !!user && (dashboardDataOld?.isApproved || !!dashboardDataNew?.metrics),
    queryFn: async () => {
      const response = await fetch(`/api/influencer/referrals?page=${currentPage}&limit=${itemsPerPage}`);
      if (!response.ok) throw new Error('Failed to fetch referrals');
      const data = await response.json();
      return data.referrals || data; // Handle different response formats
    }
  });

  // Fetch outstanding balance
  const { data: outstandingBalance } = useQuery<{ total: number; commissions: any[] }>({
    queryKey: ['/api/influencer/commissions/outstanding'],
    enabled: !!user && (dashboardDataOld?.isApproved || !!dashboardDataNew?.metrics),
    queryFn: async () => {
      const response = await fetch('/api/influencer/commissions/outstanding');
      if (!response.ok) throw new Error('Failed to fetch outstanding balance');
      return response.json();
    }
  });

  // Fetch promo codes
  const { data: promoCodes, isLoading: promoCodesLoading } = useQuery<PromoCode[]>({
    queryKey: ['/api/influencer/promo-codes'],
    enabled: !!user && (dashboardDataOld?.isApproved || !!dashboardDataNew?.metrics),
    queryFn: async () => {
      const response = await fetch('/api/influencer/promo-codes');
      if (!response.ok) throw new Error('Failed to fetch promo codes');
      return response.json();
    }
  });

  // Calculate promo code stats
  const promoCodeStats = useMemo<PromoCodeStats>(() => {
    if (!promoCodes) {
      return {
        totalCodes: 0,
        activeCodes: 0,
        inactiveCodes: 0,
        totalUses: 0,
        totalRevenue: 0,
        totalCommission: 0
      };
    }

    return {
      totalCodes: promoCodes.length,
      activeCodes: promoCodes.filter(c => c.status === 'active').length,
      inactiveCodes: promoCodes.filter(c => c.status !== 'active').length,
      totalUses: promoCodes.reduce((sum, c) => sum + c.timesRedeemed, 0),
      totalRevenue: promoCodes.reduce((sum, c) => sum + parseFloat(c.totalRevenue || '0'), 0),
      totalCommission: promoCodes.reduce((sum, c) => sum + parseFloat(c.totalCommissionEarned || '0'), 0)
    };
  }, [promoCodes]);

  // Generate monthly earnings data from actual referrals
  const monthlyEarningsData = useMemo(() => {
    const data: MonthlyEarning[] = [];
    
    if (dashboardDataNew?.metrics) {
      // Use real metrics if available
      const currentMonth = new Date();
      data.push({
        month: format(currentMonth, 'MMM'),
        earnings: dashboardDataNew.metrics.thisMonth.earned
      });
      
      const lastMonth = subMonths(currentMonth, 1);
      data.push({
        month: format(lastMonth, 'MMM'),
        earnings: dashboardDataNew.metrics.lastMonth.earned
      });
      
      // Pad with zeros for older months if we don't have data
      for (let i = 2; i < 6; i++) {
        const month = subMonths(currentMonth, i);
        data.push({
          month: format(month, 'MMM'),
          earnings: 0
        });
      }
      
      return data.reverse();
    }
    
    // If no real data, return empty array
    return [];
  }, [dashboardDataNew]);

  // Generate conversion funnel data from real metrics
  const conversionFunnelData: ConversionFunnel[] = useMemo(() => {
    if (dashboardDataNew?.metrics) {
      return [
        { name: 'Clicks', value: dashboardDataNew.metrics.lifetime.clicks, fill: '#8b5cf6' },
        { name: 'Signups', value: dashboardDataNew.metrics.lifetime.signups, fill: '#a78bfa' },
        { name: 'Conversions', value: dashboardDataNew.metrics.lifetime.conversions, fill: '#c4b5fd' }
      ];
    }
    return [
      { name: 'Clicks', value: 0, fill: '#8b5cf6' },
      { name: 'Signups', value: 0, fill: '#a78bfa' },
      { name: 'Conversions', value: 0, fill: '#c4b5fd' }
    ];
  }, [dashboardDataNew]);

  const applyMutation = useMutation({
    mutationFn: () => apiRequest('POST', '/api/influencer/apply'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/influencer/dashboard'] });
      toast({
        title: "Application Submitted",
        description: "Your influencer application has been submitted for review."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Application Failed",
        description: error?.message || "Failed to submit application",
        variant: "destructive"
      });
    }
  });

  const createAccountMutation = useMutation({
    mutationFn: () => apiRequest('POST', '/api/influencer/create-connect-account'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/influencer/account-status'] });
      setCreatingAccount(false);
      toast({
        title: "Account Created",
        description: "Your Stripe Connect account has been created. Click 'Complete Setup' to continue."
      });
    },
    onError: (error: any) => {
      setCreatingAccount(false);
      toast({
        title: "Account Creation Failed",
        description: error?.message || "Failed to create Stripe Connect account",
        variant: "destructive"
      });
    }
  });

  const getOnboardingLinkMutation = useMutation({
    mutationFn: () => apiRequest('GET', '/api/influencer/onboarding-link'),
    onSuccess: (data: { url: string }) => {
      window.location.href = data.url;
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to get onboarding link",
        variant: "destructive"
      });
    }
  });

  const requestPayoutMutation = useMutation({
    mutationFn: () => apiRequest('POST', '/api/influencer/request-payout'),
    onSuccess: () => {
      toast({
        title: "Payout Requested",
        description: "Your payout request has been submitted and will be processed within 2-3 business days."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Payout Request Failed",
        description: error?.message || "Failed to request payout",
        variant: "destructive"
      });
    }
  });

  const trackDownloadMutation = useMutation({
    mutationFn: (assetType: string) => apiRequest('POST', '/api/influencer/media/download', { assetType }),
    onSuccess: () => {
      // Silent success - just track the download
    }
  });

  const deactivatePromoCodeMutation = useMutation({
    mutationFn: (codeId: number) => apiRequest('DELETE', `/api/influencer/promo-codes/${codeId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/influencer/promo-codes'] });
      toast({
        title: "Code Deactivated",
        description: "The promo code has been deactivated successfully."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Deactivation Failed",
        description: error?.message || "Failed to deactivate promo code",
        variant: "destructive"
      });
    }
  });

  const updatePromoCodeMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: number; status?: string; description?: string }) => 
      apiRequest('PATCH', `/api/influencer/promo-codes/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/influencer/promo-codes'] });
      toast({
        title: "Code Updated",
        description: "The promo code has been updated successfully."
      });
      setEditingPromoCode(null);
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error?.message || "Failed to update promo code",
        variant: "destructive"
      });
    }
  });

  const copyReferralCode = () => {
    // Use referralLink if available (new format), otherwise construct it
    const referralUrl = dashboardDataNew?.referralLink || 
                        (dashboardData?.referralCode ? `${window.location.origin}?ref=${dashboardData.referralCode}` : '');
    
    if (referralUrl) {
      navigator.clipboard.writeText(referralUrl);
      toast({
        title: "Copied!",
        description: "Referral link copied to clipboard"
      });
    }
  };

  const copyPromoCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast({
      title: "Copied!",
      description: `Promo code ${code} copied to clipboard`
    });
  };

  const copyPromoLink = (code: string) => {
    const promoUrl = `${window.location.origin}?promo=${code}`;
    navigator.clipboard.writeText(promoUrl);
    toast({
      title: "Link Copied!",
      description: "Shareable link with promo code copied to clipboard"
    });
  };

  const sharePromoCode = (code: string, platform: 'instagram' | 'tiktok' | 'twitter' | 'facebook') => {
    const promoUrl = `${window.location.origin}?promo=${code}`;
    const message = `Save ${promoCodes?.find(p => p.code === code)?.discountPercent || 10}% on SnuggleCrafters with my code ${code}! 🧸✨`;
    
    let shareUrl = '';
    switch(platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(promoUrl)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(promoUrl)}`;
        break;
      case 'instagram':
      case 'tiktok':
        // For Instagram and TikTok, copy to clipboard with formatted message
        navigator.clipboard.writeText(`${message}\n\nLink: ${promoUrl}`);
        toast({
          title: "Content Copied!",
          description: `Paste this in your ${platform === 'instagram' ? 'Instagram' : 'TikTok'} post/story`
        });
        return;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  const exportToCSV = () => {
    if (!referralActivity || referralActivity.length === 0) {
      toast({
        title: "No Data",
        description: "No referral activity to export",
        variant: "destructive"
      });
      return;
    }
    
    const headers = ['Date', 'Event Type', 'Customer ID', 'Commission Amount', 'Commission Status'];
    const rows = referralActivity.map(activity => [
      format(new Date(activity.createdAt), 'yyyy-MM-dd HH:mm'),
      activity.eventType,
      activity.referredUserId || activity.customerEmail || '-',
      activity.commissionAmount?.toFixed(2) || '0.00',
      activity.commissionStatus || 'pending'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `referral-activity-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Export Successful",
      description: `Exported ${rows.length} referral activities to CSV`
    });
  };

  const downloadAsset = (assetType: string, assetUrl: string) => {
    trackDownloadMutation.mutate(assetType);
    const a = document.createElement('a');
    a.href = assetUrl;
    a.download = assetType;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Pagination calculations
  const paginatedActivity = useMemo(() => {
    if (!referralActivity) return [];
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return referralActivity.slice(start, end);
  }, [referralActivity, currentPage]);

  const totalPages = Math.ceil((referralActivity?.length || 0) / itemsPerPage);

  if (dashboardLoading) {
    return (
      <InfluencerLayout currentPage="Dashboard">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
        </div>
      </InfluencerLayout>
    );
  }

  if (!user) {
    setLocation('/auth');
    return null;
  }

  // Check if user is approved as influencer (works for both old and new format)
  const isApproved = dashboardDataOld?.isApproved || !!dashboardDataNew?.metrics;

  if (dashboardError || !dashboardData) {
    return (
      <InfluencerLayout currentPage="Dashboard">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Become an Influencer
              </CardTitle>
              <CardDescription>
                Join our influencer program to earn commissions by sharing SnuggleCrafters with your audience.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                data-testid="button-apply-influencer"
                onClick={() => applyMutation.mutate()}
                disabled={applyMutation.isPending}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
              >
                {applyMutation.isPending ? "Applying..." : "Apply to Become an Influencer"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </InfluencerLayout>
    );
  }

  if (!isApproved) {
    return (
      <InfluencerLayout currentPage="Dashboard">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Alert className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950">
            <HourglassIcon className="h-4 w-4 text-yellow-600" />
            <AlertDescription>
              Your influencer application is pending review. We'll notify you once it's approved.
            </AlertDescription>
          </Alert>
        </div>
      </InfluencerLayout>
    );
  }

  const needsStripeSetup = !accountStatus?.hasAccount || !accountStatus?.onboardingCompleted;
  const canRequestPayout = (outstandingBalance?.total || 0) > 50;

  return (
    <InfluencerLayout currentPage="Dashboard">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
              <BarChart3 className="w-8 h-8 text-purple-500" />
              Influencer Dashboard
              {dashboardDataNew?.badges?.some(b => b.type === 'bearfluencer') && (
                <BearFluencerBadge size="large" variant="prominent" />
              )}
            </h1>
            <p className="text-muted-foreground">
              Track your referrals and earnings
            </p>
          </div>
          <Badge variant={accountStatus?.payoutsEnabled ? "default" : "secondary"} className="text-sm px-3 py-1">
            {accountStatus?.payoutsEnabled ? (
              <>
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Payouts Active
              </>
            ) : (
              <>
                <Clock className="w-4 h-4 mr-1" />
                Setup Required
              </>
            )}
          </Badge>
        </div>

        {needsStripeSetup && (
          <Alert className="mb-6 border-orange-500 bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-950 dark:to-yellow-950">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                {!accountStatus?.hasAccount
                  ? "You need to set up your payout account to receive earnings."
                  : "Complete your Stripe onboarding to start receiving payouts."}
              </span>
              <Button
                data-testid="button-setup-payouts"
                size="sm"
                onClick={() => {
                  if (!accountStatus?.hasAccount) {
                    setCreatingAccount(true);
                    createAccountMutation.mutate();
                  } else {
                    getOnboardingLinkMutation.mutate();
                  }
                }}
                disabled={createAccountMutation.isPending || getOnboardingLinkMutation.isPending || creatingAccount}
              >
                {createAccountMutation.isPending || creatingAccount ? "Creating..." : 
                 getOnboardingLinkMutation.isPending ? "Loading..." :
                 !accountStatus?.hasAccount ? "Set Up Payouts" : "Complete Setup"}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Performance Metrics Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-purple-500 to-pink-500 text-white hover:shadow-xl transition-all transform hover:scale-105">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
              <DollarSign className="h-4 w-4 opacity-75" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="text-total-earnings">
                ${(dashboardDataNew?.metrics?.lifetime.totalEarned || dashboardDataOld?.totalEarnings || 0).toFixed(2)}
              </div>
              <p className="text-xs opacity-90 mt-1">
                ${(dashboardDataNew?.metrics?.lifetime.pendingCommission || dashboardDataOld?.pendingEarnings || 0).toFixed(2)} pending
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white hover:shadow-xl transition-all transform hover:scale-105">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Outstanding Balance</CardTitle>
              <Wallet className="h-4 w-4 opacity-75" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="text-outstanding-balance">
                ${(outstandingBalance?.total || 0).toFixed(2)}
              </div>
              <p className="text-xs opacity-90 mt-1">
                Ready for payout
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-emerald-500 text-white hover:shadow-xl transition-all transform hover:scale-105">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">This Month</CardTitle>
              <CalendarDays className="h-4 w-4 opacity-75" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="text-this-month-earnings">
                ${(dashboardDataNew?.metrics?.thisMonth.earned || 0).toFixed(2)}
              </div>
              <p className="text-xs opacity-90 mt-1">
                {format(new Date(), 'MMMM yyyy')}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-red-500 text-white hover:shadow-xl transition-all transform hover:scale-105">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Commission Rate</CardTitle>
              <Percent className="h-4 w-4 opacity-75" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="text-commission-rate">
                {((dashboardDataNew?.commissionRate || dashboardDataOld?.commissionRate || 0.10) * 100).toFixed(0)}%
              </div>
              <p className="text-xs opacity-90 mt-1">
                Per referral sale
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-indigo-500 to-purple-500 text-white hover:shadow-xl transition-all transform hover:scale-105">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Conversions</CardTitle>
              <Users className="h-4 w-4 opacity-75" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="text-total-referrals">
                {dashboardDataNew?.metrics?.lifetime.conversions || dashboardDataOld?.totalReferrals || 0}
              </div>
              <p className="text-xs opacity-90 mt-1">
                Successful conversions
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-teal-500 to-green-500 text-white hover:shadow-xl transition-all transform hover:scale-105">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Signups</CardTitle>
              <UserPlus className="h-4 w-4 opacity-75" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="text-total-conversions">
                {dashboardDataNew?.metrics?.lifetime.signups || 0}
              </div>
              <p className="text-xs opacity-90 mt-1">
                User registrations
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-pink-500 to-rose-500 text-white hover:shadow-xl transition-all transform hover:scale-105">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
              <TrendingUp className="h-4 w-4 opacity-75" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="text-conversion-rate">
                {(() => {
                  const clicks = dashboardDataNew?.metrics?.lifetime.clicks || 0;
                  const conversions = dashboardDataNew?.metrics?.lifetime.conversions || 0;
                  const rate = clicks > 0 ? (conversions / clicks) * 100 : 0;
                  return rate.toFixed(1);
                })()}%
              </div>
              <p className="text-xs opacity-90 mt-1">
                Clicks to sales
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500 to-yellow-500 text-white hover:shadow-xl transition-all transform hover:scale-105">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Clicks</CardTitle>
              <MousePointerClick className="h-4 w-4 opacity-75" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="text-total-clicks">
                {dashboardDataNew?.metrics?.lifetime.clicks || 0}
              </div>
              <p className="text-xs opacity-90 mt-1">
                Link clicks tracked
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Performance Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-500" />
                Monthly Earnings Trend
              </CardTitle>
              <CardDescription>
                Your earnings over the last 6 months
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={monthlyEarningsData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip 
                    formatter={(value: number) => `$${value.toFixed(2)}`}
                    contentStyle={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="earnings" 
                    stroke="#8b5cf6" 
                    strokeWidth={3}
                    dot={{ fill: '#8b5cf6', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-500" />
                Conversion Funnel
              </CardTitle>
              <CardDescription>
                Track your referral conversion journey
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={conversionFunnelData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip contentStyle={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }} />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {conversionFunnelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Your Referral Link */}
        <Card className="mb-8 hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-purple-500" />
              Your Referral Link
            </CardTitle>
            <CardDescription>
              Share this link with your audience to earn commissions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex-1 p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950 dark:to-pink-950 rounded-lg font-mono text-sm break-all border border-purple-200 dark:border-purple-800" data-testid="text-referral-link">
                {window.location.origin}?ref={fullDashboardData?.referralCode}
              </div>
              <Button
                data-testid="button-copy-referral"
                size="icon"
                variant="outline"
                onClick={copyReferralCode}
                className="hover:bg-purple-100 dark:hover:bg-purple-900"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Award className="w-4 h-4 text-purple-500" />
              <span>Referral Code: <code className="font-mono bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900 dark:to-pink-900 px-2 py-1 rounded">{fullDashboardData?.referralCode}</code></span>
            </div>
          </CardContent>
        </Card>

        {/* Promo Codes Management Section */}
        <Card className="mb-8 hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-purple-500" />
                Promo Code Management
              </CardTitle>
              <CardDescription>
                Create and manage discount codes for your audience
              </CardDescription>
            </div>
            <PromoCodeDialog onSuccess={() => queryClient.invalidateQueries({ queryKey: ['/api/influencer/promo-codes'] })} />
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Promo Code Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950 dark:to-pink-950 border-purple-200 dark:border-purple-800">
                <CardContent className="p-4">
                  <div className="text-xs text-muted-foreground mb-1">Total Codes</div>
                  <div className="text-2xl font-bold text-purple-700 dark:text-purple-300" data-testid="text-total-promo-codes">
                    {promoCodeStats.totalCodes}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 border-green-200 dark:border-green-800">
                <CardContent className="p-4">
                  <div className="text-xs text-muted-foreground mb-1">Active</div>
                  <div className="text-2xl font-bold text-green-700 dark:text-green-300" data-testid="text-active-promo-codes">
                    {promoCodeStats.activeCodes}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950 dark:to-cyan-950 border-blue-200 dark:border-blue-800">
                <CardContent className="p-4">
                  <div className="text-xs text-muted-foreground mb-1">Total Uses</div>
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-300" data-testid="text-total-promo-uses">
                    {promoCodeStats.totalUses}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950 dark:to-red-950 border-orange-200 dark:border-orange-800">
                <CardContent className="p-4">
                  <div className="text-xs text-muted-foreground mb-1">Revenue</div>
                  <div className="text-xl font-bold text-orange-700 dark:text-orange-300" data-testid="text-promo-revenue">
                    ${promoCodeStats.totalRevenue.toFixed(0)}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950 dark:to-purple-950 border-indigo-200 dark:border-indigo-800">
                <CardContent className="p-4">
                  <div className="text-xs text-muted-foreground mb-1">Commission</div>
                  <div className="text-xl font-bold text-indigo-700 dark:text-indigo-300" data-testid="text-promo-commission">
                    ${promoCodeStats.totalCommission.toFixed(0)}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-950 dark:to-slate-950 border-gray-200 dark:border-gray-800">
                <CardContent className="p-4">
                  <div className="text-xs text-muted-foreground mb-1">Inactive</div>
                  <div className="text-2xl font-bold text-gray-700 dark:text-gray-300" data-testid="text-inactive-promo-codes">
                    {promoCodeStats.inactiveCodes}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Stripe Connect Warning */}
            {!accountStatus?.payoutsEnabled && (
              <Alert className="mb-4 border-orange-500 bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-950 dark:to-yellow-950">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="flex items-center justify-between">
                  <span>
                    Complete your Stripe Connect setup to receive commissions from promo codes.
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => getOnboardingLinkMutation.mutate()}
                    disabled={getOnboardingLinkMutation.isPending}
                  >
                    Complete Setup
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {/* Promo Codes Table */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Your Promo Codes</h3>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <Info className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Promo codes give your audience a discount and you earn commission on their purchases.
                        Commission rate: {((dashboardData?.commissionRate || 0.10) * 100).toFixed(0)}% of order value.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              {promoCodesLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-16" />
                  <Skeleton className="h-16" />
                  <Skeleton className="h-16" />
                </div>
              ) : promoCodes && promoCodes.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Discount</TableHead>
                        <TableHead>Uses</TableHead>
                        <TableHead>Revenue</TableHead>
                        <TableHead>Commission</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {promoCodes.map((code) => (
                        <TableRow key={code.id} data-testid={`promo-code-row-${code.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <code className="font-mono font-semibold bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900 dark:to-pink-900 px-2 py-1 rounded">
                                {code.code}
                              </code>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => copyPromoCode(code.code)}
                                data-testid={`button-copy-promo-${code.id}`}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                            {code.description && (
                              <p className="text-xs text-muted-foreground mt-1">{code.description}</p>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={code.status === 'active' ? 'default' : 'secondary'}>
                              {code.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{code.discountPercent}%</TableCell>
                          <TableCell>
                            {code.timesRedeemed}
                            {code.maxRedemptions && (
                              <span className="text-xs text-muted-foreground">
                                /{code.maxRedemptions}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>${parseFloat(code.totalRevenue || '0').toFixed(2)}</TableCell>
                          <TableCell className="text-green-600 dark:text-green-400">
                            ${parseFloat(code.totalCommissionEarned || '0').toFixed(2)}
                          </TableCell>
                          <TableCell>
                            {code.expiresAt ? format(new Date(code.expiresAt), 'MMM d, yyyy') : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => {
                                        setSelectedPromoCode(code);
                                        setPromoStatsOpen(true);
                                      }}
                                      data-testid={`button-view-stats-${code.id}`}
                                    >
                                      <BarChart3 className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>View Stats</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>

                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => copyPromoLink(code.code)}
                                      data-testid={`button-copy-link-${code.id}`}
                                    >
                                      <LinkIcon className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Copy Link</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>

                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => sharePromoCode(code.code, 'twitter')}
                                      data-testid={`button-share-twitter-${code.id}`}
                                    >
                                      <Twitter className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Share on Twitter</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>

                              {code.status === 'active' && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        onClick={() => deactivatePromoCodeMutation.mutate(code.id)}
                                        disabled={deactivatePromoCodeMutation.isPending}
                                        data-testid={`button-deactivate-${code.id}`}
                                      >
                                        <X className="w-4 h-4 text-red-500" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Deactivate Code</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950 dark:to-pink-950 rounded-lg">
                  <Tag className="w-12 h-12 text-purple-400 mx-auto mb-3" />
                  <p className="text-muted-foreground mb-4">No promo codes created yet</p>
                  <PromoCodeDialog
                    trigger={
                      <Button className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Your First Promo Code
                      </Button>
                    }
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Promo Code Stats Dialog */}
        {selectedPromoCode && (
          <PromoCodeStats
            promoCode={selectedPromoCode}
            open={promoStatsOpen}
            onOpenChange={setPromoStatsOpen}
          />
        )}

        {/* Resources & Tools Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Quick Actions Card */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Quick Actions
              </CardTitle>
              <CardDescription>
                Manage your influencer account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                className="w-full justify-start"
                variant="outline"
                onClick={copyReferralCode}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Referral Link
              </Button>
              <Button 
                className="w-full justify-start"
                variant="outline"
                onClick={() => setLocation('/influencer/resources')}
              >
                <FileText className="w-4 h-4 mr-2" />
                View Media Kit
              </Button>
              {canRequestPayout && (
                <Button 
                  className="w-full justify-start"
                  variant="outline"
                  onClick={() => requestPayoutMutation.mutate()}
                  disabled={requestPayoutMutation.isPending}
                >
                  <Wallet className="w-4 h-4 mr-2" />
                  Request Payout
                </Button>
              )}
              <Button 
                className="w-full justify-start"
                variant="outline"
                onClick={() => window.open('https://connect.stripe.com/express/dashboard', '_blank')}
              >
                <Building2 className="w-4 h-4 mr-2" />
                Update Banking Info
              </Button>
            </CardContent>
          </Card>

          {/* Brand Guidelines Card */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-purple-500" />
                Brand Guidelines
              </CardTitle>
              <CardDescription>
                Key information for promoting SnuggleCrafters
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2 text-sm">Core Values</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Imagination & Creativity</li>
                  <li>• Family Bonding</li>
                  <li>• Educational Play</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2 text-sm">Approved Hashtags</h4>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary" className="text-xs">
                    <Hash className="w-3 h-3 mr-1" />
                    SnuggleTime
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    <Hash className="w-3 h-3 mr-1" />
                    StoryMagic
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    <Hash className="w-3 h-3 mr-1" />
                    CraftersKids
                  </Badge>
                </div>
              </div>
              <Button 
                className="w-full"
                variant="outline"
                size="sm"
                onClick={() => setLocation('/influencer/guidelines')}
              >
                View Full Guidelines
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Marketing Materials Preview */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5 text-purple-500" />
                Marketing Materials
              </CardTitle>
              <CardDescription>
                Download assets for your campaigns
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="aspect-square bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900 dark:to-pink-900 rounded-lg flex items-center justify-center hover:scale-105 transition-transform cursor-pointer"
                     onClick={() => downloadAsset('logo', '/logo.png')}>
                  <FileDown className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="aspect-square bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900 dark:to-cyan-900 rounded-lg flex items-center justify-center hover:scale-105 transition-transform cursor-pointer"
                     onClick={() => downloadAsset('product-images', '/product-images.zip')}>
                  <Image className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => downloadAsset('logo', '/logo.png')}
                >
                  <Download className="w-3 h-3 mr-1" />
                  Logo
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => downloadAsset('products', '/products.zip')}
                >
                  <Download className="w-3 h-3 mr-1" />
                  Products
                </Button>
              </div>
              <Button 
                className="w-full"
                variant="outline"
                size="sm"
                onClick={() => setLocation('/influencer/resources')}
              >
                View All Materials
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Account Status Section */}
        <Card className="mb-8 hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-500" />
              Account Status
            </CardTitle>
            <CardDescription>
              Your profile completion and account details
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Profile Completion Checklist */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm">Application Approved</span>
                </div>
                <div className="flex items-center gap-2">
                  {accountStatus?.payoutsEnabled ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <X className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="text-sm">Stripe Connected</span>
                </div>
                <div className="flex items-center gap-2">
                  {fullDashboardData?.socialAccountsLinked ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <X className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="text-sm">Social Accounts Added</span>
                </div>
              </div>

              <Separator />

              {/* Account Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Member Since</p>
                  <p className="font-semibold">
                    {fullDashboardData?.memberSince 
                      ? format(new Date(fullDashboardData.memberSince), 'MMM d, yyyy')
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Commission Rate</p>
                  <p className="font-semibold text-lg text-purple-600 dark:text-purple-400">
                    {((fullDashboardData?.commissionRate || 0) * 100).toFixed(0)}%
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Account Type</p>
                  <Badge className="mt-1">Premium Influencer</Badge>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex items-center gap-4 pt-2">
                <Button size="sm" variant="outline">
                  <Instagram className="w-4 h-4 mr-2" />
                  Connect Instagram
                </Button>
                <Button size="sm" variant="outline">
                  <Twitter className="w-4 h-4 mr-2" />
                  Connect Twitter
                </Button>
                <Button size="sm" variant="outline">
                  <Facebook className="w-4 h-4 mr-2" />
                  Connect Facebook
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity Table */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-500" />
                Recent Activity
              </span>
              <Button 
                size="sm" 
                variant="outline"
                onClick={exportToCSV}
                disabled={!referralActivity || referralActivity.length === 0}
              >
                <FileDown className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </CardTitle>
            <CardDescription>
              Track your referral events and commissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Event Type</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="text-right">Order Value</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedActivity.map((activity) => (
                    <TableRow key={activity.id} className="hover:bg-purple-50 dark:hover:bg-purple-950">
                      <TableCell className="font-mono text-sm">
                        {format(new Date(activity.date), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {activity.eventType === 'click' && <MousePointerClick className="w-3 h-3 mr-1" />}
                          {activity.eventType === 'signup' && <UserPlus className="w-3 h-3 mr-1" />}
                          {activity.eventType === 'purchase' && <ShoppingCart className="w-3 h-3 mr-1" />}
                          {activity.eventType}
                        </Badge>
                      </TableCell>
                      <TableCell>{activity.customer || '-'}</TableCell>
                      <TableCell className="text-right">
                        {activity.orderValue ? `$${activity.orderValue.toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {activity.commission ? `$${activity.commission.toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={
                            activity.status === 'paid' ? 'default' :
                            activity.status === 'completed' ? 'secondary' :
                            'outline'
                          }
                          className="text-xs"
                        >
                          {activity.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!paginatedActivity || paginatedActivity.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No activity yet. Share your referral link to get started!
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, referralActivity?.length || 0)} of {referralActivity?.length || 0} entries
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Separator className="my-8" />

        <div className="text-center text-sm text-muted-foreground">
          <p className="flex items-center justify-center gap-2">
            <Info className="w-4 h-4" />
            Need help? Contact support at support@snugglecrafters.com
          </p>
        </div>
      </div>
    </InfluencerLayout>
  );
}